import { DESIGN_HEIGHT, DESIGN_WIDTH, dilateQuad } from '../geometry';
import type { Quad, Vec2 } from '../geometry';
import type { SceneDefinition, SceneRegion } from './types';

/**
 * The surfaces to lay stone on: the scene's drawn regions, or — when real photography is plugged into
 * `scene.photo` — the same regions re-pinned to the corners (and outlines) measured on the photograph.
 */
export function regionsFor(scene: SceneDefinition): readonly SceneRegion[] {
  const photo = scene.photo;
  if (!photo) return scene.regions;
  return scene.regions.map((region) => {
    const measured = photo.regions[region.id];
    if (!measured) return region;
    return {
      ...region,
      quad: measured.quad,
      ...(measured.mask ? { mask: measured.mask } : {}),
    };
  });
}

/**
 * The quad a shadow or highlight polygon should cover for `region`: the plane itself is drawn a hair
 * larger than its quad (`bleed`) so neighbours overlap, and an overlay left at the exact quad would
 * show as a light outline around it.
 */
export const overlayQuad = (region: SceneRegion): Quad =>
  dilateQuad(region.quad, region.bleed ?? 0.8);

/** The outline of a region on screen: its mask when it has one, otherwise its quad. */
export function regionOutline(region: SceneRegion): readonly Vec2[] {
  return region.mask ?? region.quad;
}

/**
 * Every region's outline as one SVG path in 0 – 1 units of the frame (`clipPathUnits="objectBoundingBox"`),
 * so a highlight layer of any size can be clipped to "the stone, and only the stone".
 */
export function unionClipPath(regions: readonly SceneRegion[]): string {
  const norm = (value: number, of: number): string =>
    String(Math.round((value / of) * 100000) / 100000);
  return regions
    .map((region) => {
      const points = regionOutline(region);
      return `${points
        .map(
          ([x, y], index) =>
            `${index === 0 ? 'M' : 'L'}${norm(x, DESIGN_WIDTH)} ${norm(y, DESIGN_HEIGHT)}`,
        )
        .join(' ')} Z`;
    })
    .join(' ');
}
